import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fmtTime } from "../../lib/time";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MapPin, LogIn, LogOut, AlertCircle, Activity, ChevronRight, TrendingUp, Users, Gift } from "lucide-react";
import { RingGauge } from "../../components/RingGauge";

export default function PromoterHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/promoter/me");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/promoter/my-stats").then((r) => setMyStats(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;

  const {
    stores = [],
    schedules = {},
    current_shift: shift,
    current_store: currentStore,
  } = data || {};

  const fmtSchedule = (sid) => {
    const s = schedules[sid];
    if (!s) return null;
    const days = (s.days || []).map((d) => d.toUpperCase()).join("·");
    if (!days) return null;
    return `${days} · ${s.start || "—"} – ${s.end || "—"}`;
  };

  return (
    <div className="space-y-5">
      {myStats?.active && (
        <div className="glass-panel p-5 relative overflow-hidden fade-up" data-testid="my-performance-card">
          <div className="absolute -top-14 -left-14 w-44 h-44 rounded-full bg-sky-400/15 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-slate-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400">
              This shift · {myStats.store_name}
            </span>
          </div>
          <div className={`mt-4 grid gap-1 ${myStats.giveaway_applicable ? "grid-cols-2" : "grid-cols-3"}`}>
            <RingGauge
              testid="session-units"
              pct={
                myStats.target_units > 0
                  ? (myStats.units_sold / myStats.target_units) * 100
                  : myStats.units_sold > 0
                    ? 100
                    : 0
              }
              color={
                myStats.target_units > 0 && myStats.units_sold >= myStats.target_units
                  ? "#34C759"
                  : "#0EA5E9"
              }
              center={
                <>
                  <span className="font-display font-bold text-xl leading-none">
                    {myStats.units_sold}
                  </span>
                  {myStats.target_units > 0 && (
                    <span className="text-[10px] font-medium text-neutral-400">
                      of {myStats.target_units}
                    </span>
                  )}
                </>
              }
              sub={
                <span className="mt-1.5 text-[9px] uppercase tracking-widest text-neutral-400">
                  Units sold
                </span>
              }
            />
            <RingGauge
              testid="session-consumers"
              pct={myStats.consumers > 0 ? 100 : 0}
              color="#AF52DE"
              center={
                <>
                  <Users className="h-3.5 w-3.5 text-purple-400 mb-0.5" />
                  <span className="font-display font-bold text-xl leading-none">
                    {myStats.consumers}
                  </span>
                </>
              }
              sub={
                <span className="mt-1.5 text-[9px] uppercase tracking-widest text-neutral-400">
                  Consumers
                </span>
              }
            />
            <RingGauge
              testid="session-conversion"
              pct={myStats.conversion}
              color={
                myStats.target_conversion > 0 && myStats.conversion < myStats.target_conversion
                  ? "#FF9500"
                  : "#34C759"
              }
              center={
                <>
                  <span className="font-display font-bold text-xl leading-none">
                    {myStats.conversion}%
                  </span>
                  {myStats.target_conversion > 0 && (
                    <span className="text-[10px] font-medium text-neutral-400">
                      goal {myStats.target_conversion}%
                    </span>
                  )}
                </>
              }
              sub={
                <span className="mt-1.5 text-[9px] uppercase tracking-widest text-neutral-400">
                  Conversion
                </span>
              }
            />
            {myStats.giveaway_applicable && (
              <RingGauge
                testid="session-cases"
                pct={
                  myStats.cases_target > 0
                    ? (myStats.cases_given / myStats.cases_target) * 100
                    : myStats.cases_given > 0
                      ? 100
                      : 0
                }
                color={
                  myStats.cases_target > 0 && myStats.cases_given >= myStats.cases_target
                    ? "#34C759"
                    : "#10B981"
                }
                center={
                  <>
                    <Gift className="h-3.5 w-3.5 text-emerald-500 mb-0.5" />
                    <span className="font-display font-bold text-xl leading-none">
                      {myStats.cases_given}
                    </span>
                    {myStats.cases_target > 0 && (
                      <span className="text-[10px] font-medium text-neutral-400">
                        of {myStats.cases_target}
                      </span>
                    )}
                  </>
                }
                sub={
                  <span className="mt-1.5 text-[9px] uppercase tracking-widest text-neutral-400">
                    Silicone cases
                  </span>
                }
              />
            )}
          </div>
          {myStats.target_units > 0 && myStats.units_sold >= myStats.target_units && (
            <div className="mt-3 text-center text-[10px] uppercase tracking-widest text-emerald-600 font-semibold" data-testid="target-reached">
              Sales target reached — great job!
            </div>
          )}
        </div>
      )}
      {shift ? (
        <div className="glass-panel p-5 relative overflow-hidden fade-up" data-testid="shift-status-card">
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-emerald-400/20 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 pulse-dot" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-emerald-700">
              Active shift
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />
            <span className="font-display font-bold text-xl">
              {currentStore?.name || "—"}
            </span>
          </div>
          {currentStore?.address && (
            <div className="text-sm text-neutral-500 mt-1">{currentStore.address}</div>
          )}
          <div className="text-sm text-neutral-500 mt-1 font-mono">
            In since {fmtTime(shift.check_in_at)}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Link to="/promoter/stock">
              <Button
                className="rounded-full w-full h-12 bg-slate-900 hover:bg-slate-700 text-white"
                data-testid="goto-stock"
              >
                Update stock
              </Button>
            </Link>
            <Link to="/promoter/survey">
              <Button
                variant="outline"
                className="rounded-full w-full h-12"
                data-testid="goto-survey"
              >
                Log survey
              </Button>
            </Link>
          </div>
          <Link to="/promoter/check-out">
            <Button
              variant="outline"
              className="rounded-full w-full h-12 border-red-300/70 text-red-600 hover:bg-red-50 mt-3"
              data-testid="goto-check-out"
            >
              <LogOut className="h-4 w-4 mr-1" /> End shift / check out
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="glass-panel p-5 relative overflow-hidden fade-up">
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-sky-400/20 blur-2xl pointer-events-none" />
            <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400">
              Shift status
            </div>
            <div className="mt-2 font-display font-bold text-xl">Not checked in</div>
            <div className="text-sm text-neutral-500 mt-1">
              Choose a store below to start your shift.
            </div>
          </div>

          <div className="fade-up fade-up-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Assigned stores ({stores.length})
            </div>
            {stores.length === 0 ? (
              <div className="glass-panel p-5 flex items-center gap-2 text-sm text-amber-700">
                <AlertCircle className="h-4 w-4" />
                No store assigned. Contact your admin.
              </div>
            ) : (
              <div
                className="glass-panel divide-y divide-slate-200/60 max-h-[60vh] overflow-y-auto"
                data-testid="assigned-stores-list"
              >
                {stores.map((s) => {
                  const schedLine = fmtSchedule(s.id);
                  return (
                    <Link
                      key={s.id}
                      to={`/promoter/check-in?store=${s.id}`}
                      className="flex items-center justify-between p-4 hover:bg-white/50 transition-colors"
                      data-testid={`assigned-store-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-lg truncate">{s.name}</div>
                        <div className="text-sm text-neutral-500 truncate">{s.address}</div>
                        {schedLine && (
                          <div className="font-mono text-[10px] uppercase tracking-widest text-black/70 mt-1">
                            {schedLine}
                          </div>
                        )}
                        <div className="font-mono text-[10px] text-neutral-400 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {s.latitude.toFixed(4)}, {s.longitude.toFixed(4)} · {s.radius_m}m
                        </div>
                      </div>
                      <div className="ml-3 flex flex-col items-center">
                        <div className="w-11 h-11 rounded-full bg-slate-900 text-white shadow-md shadow-slate-900/20 flex items-center justify-center">
                          <LogIn className="h-4 w-4" />
                        </div>
                        <div className="text-[9px] font-mono uppercase tracking-widest mt-1 text-neutral-500">
                          Check in
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
